/**
 * Phase 8 · C-37 (UI) — Turn-Around Time chip.
 *
 * Reusable pill that shows how a requisition (or any position) is tracking
 * against its target close date.  Consumes only two data-points that are
 * always available from the base table after migration 104:
 *   • targetCloseDate  — ISO date string (YYYY-MM-DD)
 *   • closedAt         — ISO timestamp (when the req was filled / cancelled)
 *
 * Shape spec (§33.1):
 *   rounded-full · 11 px text · 1 px border · no shadow · 10 px h-pad
 *   Leading ClockIcon (12 px) · trailing label
 *
 * Colour palette matches Wrangle design tokens:
 *   on_track → emerald   at_risk → amber   breached → red   closed → zinc
 */
import { Clock } from 'lucide-react';

// ── Public types ────────────────────────────────────────────────────────────

export type TatStatus = 'on_track' | 'at_risk' | 'breached' | 'closed';

export interface TatChipProps {
  /** ISO date string (YYYY-MM-DD). Pass null to hide the chip. */
  targetCloseDate: string | null;
  /** ISO timestamp. When set the chip flips to "closed" state. */
  closedAt?: string | null;
  /** Hard-override computed status (useful when the DB breach flag is already known). */
  status?: TatStatus;
  /** Omit label padding — e.g. when rendered inside a narrow table cell. */
  compact?: boolean;
}

// ── Status computation ───────────────────────────────────────────────────────

const MS_PER_DAY = 86_400_000;

export function computeTatStatus(
  targetCloseDate: string | null,
  closedAt?: string | null,
): TatStatus {
  if (closedAt) return 'closed';
  if (!targetCloseDate) return 'on_track';

  // Compare at day-granularity so time-of-day doesn't cause flickering.
  const todayStr = new Date().toISOString().slice(0, 10);
  const today = new Date(todayStr).getTime();
  const target = new Date(targetCloseDate).getTime();

  if (today > target) return 'breached';
  const daysLeft = (target - today) / MS_PER_DAY;
  if (daysLeft <= 3) return 'at_risk';
  return 'on_track';
}

// ── Label helper ─────────────────────────────────────────────────────────────

function chipLabel(
  status: TatStatus,
  targetCloseDate: string | null,
  closedAt?: string | null,
): string {
  if (status === 'closed') {
    const ts = closedAt ?? targetCloseDate;
    if (!ts) return 'Closed';
    const d = new Date(ts);
    // "Closed on 15 Apr" format
    return `Closed on ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
  }

  if (!targetCloseDate) return '—';

  const todayStr = new Date().toISOString().slice(0, 10);
  const today = new Date(todayStr).getTime();
  const target = new Date(targetCloseDate).getTime();

  if (status === 'breached') {
    const daysPast = Math.max(1, Math.floor((today - target) / MS_PER_DAY));
    return `Breached ${daysPast}d`;
  }

  // on_track or at_risk
  const daysLeft = Math.ceil((target - today) / MS_PER_DAY);
  return `${daysLeft}d left`;
}

// ── Visual tokens ─────────────────────────────────────────────────────────────

const CHIP_STYLE: Record<TatStatus, string> = {
  on_track: [
    'bg-emerald-50 text-emerald-700 border-emerald-200',
    'dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800',
  ].join(' '),
  at_risk: [
    'bg-amber-50 text-amber-700 border-amber-200',
    'dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
  ].join(' '),
  breached: [
    'bg-red-50 text-red-700 border-red-200',
    'dark:bg-red-900/30 dark:text-red-300 dark:border-red-800',
  ].join(' '),
  closed: [
    'bg-zinc-100 text-zinc-600 border-zinc-200',
    'dark:bg-zinc-800/60 dark:text-zinc-400 dark:border-zinc-700',
  ].join(' '),
};

/** Progress bar fill colour (Tailwind bg class) per status. */
export const TAT_PROGRESS_COLOR: Record<TatStatus, string> = {
  on_track: 'bg-emerald-500',
  at_risk:  'bg-amber-500',
  breached: 'bg-red-500',
  closed:   'bg-zinc-400',
};

// ── Component ────────────────────────────────────────────────────────────────

export function TatChip({
  targetCloseDate,
  closedAt,
  status: statusOverride,
  compact = false,
}: TatChipProps) {
  // Nothing to show when both dates are absent.
  if (!targetCloseDate && !closedAt) return null;

  const status = statusOverride ?? computeTatStatus(targetCloseDate, closedAt);
  const label  = chipLabel(status, targetCloseDate, closedAt);

  return (
    <span
      className={[
        'inline-flex items-center gap-1 rounded-full border',
        compact ? 'px-[8px] py-px' : 'px-[10px] py-0.5',
        'text-[11px] font-medium leading-none whitespace-nowrap',
        CHIP_STYLE[status],
      ].join(' ')}
    >
      <Clock className="h-3 w-3 shrink-0" aria-hidden />
      {label}
    </span>
  );
}
